import { useId, useState, useRef, useEffect, useCallback, type CSSProperties } from 'react';
import { useAnimationFrame } from '../viz/useAnimationFrame';
import {
  usePrefersReducedMotion,
  cueIsActive,
  cueHighlights,
  type VizCue,
} from '../viz/vizShared';

// Digital pH probe with a colour-coded reading, a universal-indicator gradient
// scale, and a "dip into solution" interaction with a settling response curve.
// Instrument body colors are physical; accents use --ws-* tokens.
//
// CUE CONTRACT (optional, additive): cue.progress (0..1) maps to pH 0..14;
// cue.play=true dips the probe; highlight token a numeric like 'ph:4.2' or a
// bare number sets the target pH. The probe reading eases toward its target so
// the digital display animates like a real meter stabilising.

export interface PHMeterProps {
  reading?: number; // pH 0-14
  inSolution?: boolean;
  onDip?: () => void;
  disabled?: boolean;
  className?: string;
  /** Narration-sync cue forwarded by the lecture player. */
  cue?: VizCue;
}

// Universal indicator colour at a given pH (smooth blend of the standard stops).
const PH_STOPS: Array<[number, [number, number, number]]> = [
  [0, [211, 47, 47]], // red
  [2, [229, 96, 43]], // orange-red
  [4, [245, 166, 35]], // orange
  [6, [205, 220, 57]], // yellow-green
  [7, [76, 175, 80]], // green
  [8, [0, 168, 107]], // teal-green
  [10, [33, 150, 243]], // blue
  [12, [63, 81, 181]], // indigo
  [14, [123, 31, 162]], // violet
];

function lerp(a: number, b: number, t: number) {
  return Math.round(a + (b - a) * t);
}
function pHColor(pH: number): string {
  const p = Math.max(0, Math.min(14, pH));
  for (let i = 0; i < PH_STOPS.length - 1; i++) {
    const [p0, c0] = PH_STOPS[i];
    const [p1, c1] = PH_STOPS[i + 1];
    if (p >= p0 && p <= p1) {
      const t = (p - p0) / (p1 - p0);
      return `rgb(${lerp(c0[0], c1[0], t)},${lerp(c0[1], c1[1], t)},${lerp(c0[2], c1[2], t)})`;
    }
  }
  return 'rgb(123,31,162)';
}

function nature(pH: number): string {
  if (pH < 6.5) return 'ACID';
  if (pH > 7.5) return 'BASE';
  return 'NEUTRAL';
}

export default function PHMeter({
  reading = 7.0,
  inSolution = false,
  onDip,
  disabled = false,
  className = '',
  cue,
}: PHMeterProps) {
  const id = useId();
  const reduced = usePrefersReducedMotion();
  const cueOn = cueIsActive(cue);
  const tokens = cueHighlights(cue);
  const [isHovered, setIsHovered] = useState(false);

  // Target pH: cue.progress maps 0..1 → 0..14; a numeric highlight overrides.
  const numToken = tokens
    .map((t) => {
      const m = t.match(/(\d+(\.\d+)?)/);
      return m ? parseFloat(m[1]) : NaN;
    })
    .find((n) => !Number.isNaN(n) && n >= 0 && n <= 14);
  const cuePH = cue?.progress != null ? cue.progress * 14 : numToken;
  const targetPH = cuePH != null ? cuePH : reading;

  // Dip state: own it so the meter can self-demonstrate / follow cue.play.
  const [dipped, setDipped] = useState(inSolution);
  useEffect(() => setDipped(inSolution), [inSolution]);
  const cueDip = cueOn && (cue?.play === true || cuePH != null);
  const active = cueDip ? true : dipped;

  // Eased display reading — animates toward target while in solution.
  const dispRef = useRef(active ? targetPH : 7);
  const [, setTick] = useState(0);
  const bump = useCallback(() => setTick((n) => (n + 1) % 1_000_000), []);
  useAnimationFrame((dt) => {
    const want = active ? targetPH : 7;
    const cur = dispRef.current;
    const next = cur + (want - cur) * Math.min(1, dt * 6);
    dispRef.current = Math.abs(next - want) < 0.005 ? want : next;
    bump();
  }, !reduced);
  const display = reduced ? (active ? targetPH : 7) : dispRef.current;
  const showColor = active ? pHColor(display) : '#00A86B';

  const containerStyle: CSSProperties = {
    cursor: disabled ? 'default' : onDip ? 'pointer' : 'default',
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.2s ease',
  };

  const handleDip = () => {
    if (disabled) return;
    onDip?.();
    setDipped((d) => !d);
  };

  // Marker position along the gradient scale (x: 8..72).
  const markerX = 8 + (Math.max(0, Math.min(14, display)) / 14) * 64;

  return (
    <svg
      viewBox="0 0 80 196"
      width={108}
      height={264}
      style={containerStyle}
      className={className}
      onClick={onDip ? handleDip : undefined}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="img"
      aria-label={`pH meter reading ${display.toFixed(2)}`}
    >
      <defs>
        <linearGradient id={`body-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#3A3A3A" />
          <stop offset="20%" stopColor="#4A4A4A" />
          <stop offset="80%" stopColor="#4A4A4A" />
          <stop offset="100%" stopColor="#3A3A3A" />
        </linearGradient>
        <linearGradient id={`probe-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#5A5A5A" />
          <stop offset="30%" stopColor="#8A8A8A" />
          <stop offset="70%" stopColor="#8A8A8A" />
          <stop offset="100%" stopColor="#5A5A5A" />
        </linearGradient>
        <linearGradient id={`electrode-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(180,200,220,0.8)" />
          <stop offset="30%" stopColor="rgba(255,255,255,0.5)" />
          <stop offset="70%" stopColor="rgba(255,255,255,0.4)" />
          <stop offset="100%" stopColor="rgba(180,200,220,0.7)" />
        </linearGradient>
        {/* Universal-indicator colour scale. */}
        <linearGradient id={`phscale-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
          {PH_STOPS.map(([p, c]) => (
            <stop key={p} offset={`${(p / 14) * 100}%`} stopColor={`rgb(${c[0]},${c[1]},${c[2]})`} />
          ))}
        </linearGradient>
        <filter id={`glow-${id}`} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="1.5" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <filter id={`shadow-${id}`} x="-15%" y="-5%" width="130%" height="115%">
          <feDropShadow dx="2" dy="3" stdDeviation="2" floodOpacity="0.3" />
        </filter>
      </defs>

      {isHovered && onDip && (
        <rect x="8" y="3" width="64" height="180" fill="none" stroke="var(--ws-accent)" strokeWidth="2" rx="10" opacity="0.5" />
      )}

      <g filter={`url(#shadow-${id})`}>
        <rect x="13" y="8" width="54" height="92" fill={`url(#body-${id})`} rx="8" />
        <rect x="13" y="8" width="54" height="12" fill="#4A4A4A" rx="8" />
        <rect x="13" y="16" width="54" height="4" fill="#4A4A4A" />

        {/* LCD. */}
        <rect x="18" y="24" width="44" height="40" fill="#1A1A1A" stroke="#3A3A3A" strokeWidth="1" rx="3" />
        <rect x="21" y="27" width="38" height="24" fill="#001a00" rx="2" />

        <g filter={`url(#glow-${id})`}>
          <text x="40" y="44" fontSize="15" fill={showColor} textAnchor="middle" fontFamily="'Courier New', monospace" fontWeight="bold">
            {display.toFixed(2)}
          </text>
        </g>
        <text x="24" y="49" fontSize="6" fill="#888" textAnchor="start" fontFamily="Arial, sans-serif">pH</text>
        <text x="56" y="49" fontSize="5" fill={active ? showColor : '#666'} textAnchor="end" fontFamily="Arial, sans-serif" fontWeight="bold">
          {active ? nature(display) : '—'}
        </text>

        {/* Universal-indicator gradient scale with a live marker. */}
        <rect x="8" y="56" width="64" height="5" rx="2.5" fill={`url(#phscale-${id})`} stroke="#2A2A2A" strokeWidth="0.5" />
        <g transform={`translate(${markerX} 0)`} style={{ transition: reduced ? undefined : 'transform 80ms linear' }}>
          <polygon points="0,53 -2.4,49 2.4,49" fill={active ? '#FFFFFF' : '#666'} stroke="#000" strokeWidth="0.3" />
          <rect x="-0.6" y="53" width="1.2" height="9" fill={active ? '#FFFFFF' : '#555'} opacity="0.9" />
        </g>

        {/* Controls. */}
        <g>
          <circle cx="22" cy="84" r="5" fill="#2A2A2A" stroke="#5A5A5A" strokeWidth="1" />
          <circle cx="22" cy="84" r="2" fill={active ? '#00E676' : 'var(--ws-accent)'} />
          <rect x="31" y="79" width="14" height="9" fill="#3A3A3A" rx="2" />
          <text x="38" y="85.5" fontSize="5" fill="#888" textAnchor="middle">CAL</text>
          <rect x="48" y="79" width="14" height="9" fill="#3A3A3A" rx="2" />
          <text x="55" y="85.5" fontSize="5" fill="#888" textAnchor="middle">HOLD</text>
        </g>
        <text x="40" y="96" fontSize="6" fill="#666" textAnchor="middle" fontFamily="'Courier New', monospace">
          25.0&#176;C
        </text>

        {/* Probe stem + glass electrode. */}
        <rect x="35" y="100" width="10" height="8" fill="#3A3A3A" rx="2" />
        <rect x="33" y="108" width="14" height="46" fill={`url(#probe-${id})`} rx="3" />
        <line x1="35" y1="116" x2="45" y2="116" stroke="#4A4A4A" strokeWidth="0.5" />
        <line x1="35" y1="126" x2="45" y2="126" stroke="#4A4A4A" strokeWidth="0.5" />
        <line x1="35" y1="136" x2="45" y2="136" stroke="#4A4A4A" strokeWidth="0.5" />
        <line x1="35" y1="146" x2="45" y2="146" stroke="#4A4A4A" strokeWidth="0.5" />
        <path d="M35 154 L35 170 Q35 176 40 176 Q45 176 45 170 L45 154" fill={`url(#electrode-${id})`} stroke="#7A9BB8" strokeWidth="1" />
        {/* Bulb tints with the measured pH colour when active. */}
        <ellipse cx="40" cy="173" rx="4.4" ry="3.4" fill={active ? showColor : 'rgba(200,220,240,0.7)'} opacity={active ? 0.55 : 1} stroke="#7A9BB8" strokeWidth="0.5" />
        <rect x="38" y="156" width="4" height="14" fill="rgba(192,192,192,0.5)" rx="1" />

        {active && (
          <>
            <ellipse cx="40" cy="176" rx="6" ry="3" fill={showColor} opacity="0.4">
              <animate attributeName="rx" values="6;9;6" dur="1.2s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.4;0.1;0.4" dur="1.2s" repeatCount="indefinite" />
            </ellipse>
            {/* Solution surface line. */}
            <line x1="20" y1="178" x2="60" y2="178" stroke={showColor} strokeWidth="1" opacity="0.5" />
          </>
        )}

        <line x1="35" y1="113" x2="35" y2="150" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" strokeLinecap="round" />
      </g>

      <text x="40" y="192" fontSize="9" fill="var(--ws-ink-2)" textAnchor="middle" fontWeight="500">
        pH Meter
      </text>
    </svg>
  );
}
